"use client";

import { useState, type FormEvent } from "react";
import { Section } from "@astryxdesign/core/Section";
import { Center } from "@astryxdesign/core/Center";
import { VStack } from "@astryxdesign/core/VStack";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { Icon } from "@astryxdesign/core/Icon";
import { LandingSectionBg } from "@/components/landing/landing-section-bg";
import { LandingCanvasText } from "@/components/landing/landing-canvas-text";
import {
  LeadFormFields,
  type LeadFormFieldErrors,
  type LeadFormFieldState,
} from "@/components/landing/lead-form-fields";
import {
  isAgentInvokedSubmit,
  leadToolDescription,
  leadToolName,
  readLeadFormValues,
  respondToAgentSubmit,
} from "@/lib/webmcp/lead-form";
import { readBitrix24GuestTrace } from "@/lib/bitrix24/tracking";
import { readStoredUtm } from "@/lib/analytics/utm";
import { validateLeadFormValues, publicFieldsToFieldMap } from "@/lib/bitrix24/lead-form-fields";
import { useLeadFormConfig } from "@/lib/landing/use-lead-form-config";
import {
  formatRuPhoneInput,
  ruPhoneToE164,
  ruPhoneValidationError,
} from "@/lib/phone/ru-phone";
import { researchIcons } from "@/lib/icons/research-icons";
import {
  landingTextProps,
  styledTextString,
  styledTextStyle,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";

export type LandingLeadFormViewProps = {
  sectionId?: string;
  formCaseKey?: string;
  title?: LandingTextValue;
  subtitle?: LandingTextValue;
  showName?: boolean;
  showEmail?: boolean;
  showPhone?: boolean;
  showCompany?: boolean;
  showMessage?: boolean;
  submitLabel?: LandingTextValue;
  backgroundImageUrl?: string;
  backgroundColor?: string;
};

const EMPTY: LeadFormFieldState = {
  name: "",
  email: "",
  phone: "",
  company: "",
  message: "",
};

function validateEmail(email: string): string | null {
  const trimmed = email.trim();
  if (!trimmed) return "Укажите email";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return "Некорректный email";
  }
  return null;
}

export function LandingLeadFormView({
  sectionId = "lead-form",
  formCaseKey = "enterprise-request",
  title = "Оставить заявку",
  subtitle = "Оставьте контакты — обсудим условия и вернёмся с предложением.",
  showName = true,
  showEmail = true,
  showPhone = true,
  showCompany = true,
  showMessage = true,
  submitLabel = "Отправить",
  backgroundImageUrl = "",
  backgroundColor,
}: LandingLeadFormViewProps) {
  const [values, setValues] = useState<LeadFormFieldState>(EMPTY);
  const [errors, setErrors] = useState<LeadFormFieldErrors>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const formConfig = useLeadFormConfig(formCaseKey, true);
  const ui =
    formConfig?.ui ?? {
      showName,
      showEmail,
      showPhone,
      showCompany,
      showMessage,
      nameRequired: showName,
      emailRequired: showEmail,
      phoneRequired: false,
      companyRequired: false,
      messageRequired: false,
    };

  function patchValue<K extends keyof LeadFormFieldState>(
    key: K,
    value: LeadFormFieldState[K],
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: null }));
    }
  }

  function validateFields(next: LeadFormFieldState): LeadFormFieldErrors {
    if (formConfig?.fields) {
      return validateLeadFormValues(publicFieldsToFieldMap(formConfig.fields), next);
    }
    const nextErrors: LeadFormFieldErrors = {};
    if (ui.showName && ui.nameRequired && !next.name.trim()) {
      nextErrors.name = "Укажите имя";
    }
    if (ui.showEmail) {
      const emailErr = validateEmail(next.email);
      if (emailErr && ui.emailRequired) nextErrors.email = emailErr;
      else if (next.email.trim() && emailErr) nextErrors.email = emailErr;
    }
    if (ui.showPhone && (ui.phoneRequired || next.phone.trim())) {
      const phoneErr = ruPhoneValidationError(next.phone);
      if (phoneErr) nextErrors.phone = phoneErr;
    }
    return nextErrors;
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const agent = isAgentInvokedSubmit(e);
    const raw = readLeadFormValues(e.currentTarget);
    const nextValues: LeadFormFieldState = {
      name: raw.name,
      email: raw.email,
      phone: formatRuPhoneInput(raw.phone),
      company: raw.company,
      message: raw.message,
    };
    setValues(nextValues);

    const fieldErrors = validateFields(nextValues);
    if (Object.values(fieldErrors).some(Boolean)) {
      setErrors(fieldErrors);
      return;
    }

    setPending(true);
    setError(null);
    setSuccess(null);

    const phoneE164 = ruPhoneToE164(nextValues.phone);

    const work = (async () => {
      const res = await fetch(
        `/api/public/lead-forms/${encodeURIComponent(formCaseKey)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: ui.showName ? nextValues.name : "Заявка",
            email: ui.showEmail ? nextValues.email : "noreply@idealitika.ru",
            phone: ui.showPhone && phoneE164 ? phoneE164 : undefined,
            company:
              ui.showCompany && nextValues.company
                ? nextValues.company
                : undefined,
            message:
              ui.showMessage && nextValues.message
                ? nextValues.message
                : undefined,
            trace: readBitrix24GuestTrace(),
            utm: readStoredUtm(),
          }),
        },
      );
      const data = (await res.json().catch(() => null)) as {
        error?: string;
        message?: string;
      } | null;
      if (!res.ok) {
        throw new Error(data?.error || "Не удалось отправить");
      }
      return data?.message || "Заявка отправлена";
    })();

    if (agent) {
      respondToAgentSubmit(
        e,
        work.then(
          (msg) => msg,
          (err) =>
            err instanceof Error ? err.message : "Не удалось отправить заявку",
        ),
      );
    }

    try {
      const msg = await work;
      setSuccess(msg);
      setValues(EMPTY);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ошибка");
    } finally {
      setPending(false);
    }
  }

  return (
    <LandingSectionBg backgroundImageUrl={backgroundImageUrl} backgroundColor={backgroundColor}>
      <Section id={sectionId} variant="transparent" paddingBlock={10}>
        <Center>
          <div className="w-full max-w-2xl px-4 sm:px-6">
            <div className="overflow-hidden rounded-xl border border-border bg-[var(--color-background-body)] shadow-xl">
              <div className="border-b border-border px-5 py-5 sm:px-6">
                <VStack gap={1} align="start">
                  <LandingCanvasText field="title" value={title} label="Заголовок" multiline>
                    <Heading level={2} type="display-3" {...landingTextProps(title)} />
                  </LandingCanvasText>
                  <LandingCanvasText
                    field="subtitle"
                    value={subtitle}
                    label="Подзаголовок"
                    multiline
                    hideIfEmpty
                  >
                    <Text color="secondary" size="sm" {...landingTextProps(subtitle)} />
                  </LandingCanvasText>
                </VStack>
              </div>

              <div className="px-5 py-5 sm:px-6">
                  {error ? (
                    <Banner status="error" title={error} container="card" />
                  ) : null}

                  {success ? (
                    <VStack gap={3} align="center" className="py-8 text-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">
                        <Icon icon={researchIcons.checkCircle} size="lg" />
                      </div>
                      <Text weight="semibold">{success}</Text>
                    </VStack>
                  ) : (
                    <form
                      className="w-full"
                      toolname={leadToolName(formCaseKey)}
                      tooldescription={leadToolDescription(styledTextString(title))}
                      onSubmit={(ev) => void submit(ev)}
                    >
                      <VStack gap={4} width="100%">
                        <LeadFormFields
                          values={values}
                          onChange={patchValue}
                          errors={errors}
                          showName={ui.showName}
                          showEmail={ui.showEmail}
                          showPhone={ui.showPhone}
                          showCompany={ui.showCompany}
                          showMessage={ui.showMessage}
                          nameRequired={ui.nameRequired}
                          emailRequired={ui.emailRequired}
                          phoneRequired={ui.phoneRequired}
                          companyRequired={ui.companyRequired}
                          messageRequired={ui.messageRequired}
                          onPhoneBlur={() => {
                            if (!ui.showPhone) return;
                            setErrors((prev) => ({
                              ...prev,
                              phone: ruPhoneValidationError(values.phone),
                            }));
                          }}
                        />

                        <LandingCanvasText
                          field="submitLabel"
                          value={submitLabel}
                          label="Текст кнопки"
                          inline
                        >
                          <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            label={
                              pending ? "Отправка…" : styledTextString(submitLabel)
                            }
                            isLoading={pending}
                            className="w-full"
                            style={pending ? undefined : styledTextStyle(submitLabel)}
                          />
                        </LandingCanvasText>
                      </VStack>
                    </form>
                  )}
              </div>
            </div>
          </div>
        </Center>
      </Section>
    </LandingSectionBg>
  );
}
